import { Component, Inject } from '@angular/core';
import { MAT_SNACK_BAR_DATA, MatSnackBarRef } from '@angular/material/snack-bar';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastData {
  message: string;
  title?: string;
  type: ToastType;
  actionLabel?: string;
  onAction?: () => void;
}

@Component({
  selector: 'app-custom-snackbar',
  standalone: false,
  template: `
    <div class="toast-container" [ngClass]="'toast-' + data.type">
      <!-- Accent bar on left -->
      <div class="toast-accent"></div>

      <!-- Icon -->
      <div class="toast-icon">
        <span class="icon-circle">
          <mat-icon>{{ iconMap[data.type] }}</mat-icon>
        </span>
      </div>

      <!-- Content -->
      <div class="toast-content">
        <span class="toast-title">{{ data.title || titleMap[data.type] }}</span>
        <span class="toast-message">{{ data.message }}</span>
        
        <button *ngIf="data.actionLabel" class="toast-action-btn" (click)="triggerAction()">
          {{ data.actionLabel }}
        </button>
      </div>

      <!-- Close -->
      <button class="toast-close" (click)="dismiss()">
        <mat-icon>close</mat-icon>
      </button>
    </div>
  `,
  styles: [
    `
      .toast-container {
        display: flex;
        align-items: center;
        min-width: 320px;
        max-width: 480px;
        padding: 0;
        border-radius: 12px;
        overflow: hidden;
        box-shadow:
          0 8px 32px rgba(0, 0, 0, 0.18),
          0 2px 8px rgba(0, 0, 0, 0.08);
        font-family: 'Outfit', 'Roboto', sans-serif;
        animation: slideInUp 0.4s cubic-bezier(0.16, 1, 0.3, 1);
        position: relative;
      }

      @keyframes slideInUp {
        from {
          opacity: 0;
          transform: translateY(20px) scale(0.96);
        }
        to {
          opacity: 1;
          transform: translateY(0) scale(1);
        }
      }

      /* ── Accent bar ── */
      .toast-accent {
        width: 5px;
        align-self: stretch;
        flex-shrink: 0;
      }

      /* ── Icon ── */
      .toast-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 16px 0 16px 16px;
        flex-shrink: 0;
      }

      .icon-circle {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 38px;
        height: 38px;
        border-radius: 50%;
        background: rgba(255, 255, 255, 0.2);
      }

      .icon-circle mat-icon {
        font-size: 22px;
        width: 22px;
        height: 22px;
        color: #fff;
      }

      /* ── Content ── */
      .toast-content {
        display: flex;
        flex-direction: column;
        padding: 14px 12px;
        flex: 1;
        min-width: 0;
      }

      .toast-title {
        font-weight: 700;
        font-size: 14px;
        color: #fff;
        letter-spacing: 0.2px;
        margin-bottom: 2px;
      }

      .toast-message {
        font-size: 13px;
        color: rgba(255, 255, 255, 0.9);
        line-height: 1.4;
        word-break: break-word;
      }

      /* ── Close ── */
      .toast-close {
        display: flex;
        align-items: center;
        justify-content: center;
        background: transparent;
        border: none;
        cursor: pointer;
        padding: 8px;
        margin-right: 8px;
        border-radius: 50%;
        transition: background 0.2s;
        flex-shrink: 0;
      }

      .toast-close:hover {
        background: rgba(255, 255, 255, 0.15);
      }

      .toast-close mat-icon {
        font-size: 18px;
        width: 18px;
        height: 18px;
        color: rgba(255, 255, 255, 0.8);
      }

      .toast-action-btn {
        margin-top: 8px;
        background: rgba(255, 255, 255, 0.2);
        border: 1px solid rgba(255, 255, 255, 0.4);
        border-radius: 4px;
        color: white;
        padding: 4px 8px;
        cursor: pointer;
        font-size: 12px;
        align-self: flex-start;
        display: none;
        transition: background 0.2s;
      }
      button.toast-action-btn[class] {
        display: inline-block;
      }
      .toast-action-btn:hover {
        background: rgba(255, 255, 255, 0.3);
      }

      /* ── Theme variants ── */
      .toast-success {
        background: linear-gradient(135deg, #43a047, #66bb6a);
      }
      .toast-success .toast-accent {
        background: #2e7d32;
      }

      .toast-error {
        background: linear-gradient(135deg, #e53935, #ef5350);
      }
      .toast-error .toast-accent {
        background: #b71c1c;
      }

      .toast-warning {
        background: linear-gradient(135deg, #f57c00, #ffa726);
      }
      .toast-warning .toast-accent {
        background: #e65100;
      }

      .toast-info {
        background: linear-gradient(135deg, #1e88e5, #42a5f5);
      }
      .toast-info .toast-accent {
        background: #0d47a1;
      }
    `,
  ],
})
export class CustomSnackbarComponent {
  iconMap: Record<ToastType, string> = {
    success: 'check_circle',
    error: 'error',
    warning: 'warning',
    info: 'info',
  };

  titleMap: Record<ToastType, string> = {
    success: 'Success',
    error: 'Error',
    warning: 'Warning',
    info: 'Info',
  };

  constructor(
    @Inject(MAT_SNACK_BAR_DATA) public data: ToastData,
    private snackBarRef: MatSnackBarRef<CustomSnackbarComponent>,
  ) {}

  triggerAction() {
    if (this.data.onAction) {
      this.data.onAction();
    }
  }

  dismiss() {
    this.snackBarRef.dismissWithAction();
  }
}
